'use client';

import { createClient } from '@/lib/supabase/client';
import { calculateEMI, generateAmortization, getEMIStatus } from '@/lib/formatters';

export interface Loan {
  id: string;
  userId: string;
  name: string;
  lender: string;
  type: 'Home' | 'Car' | 'Personal' | 'Credit Card' | 'Phone' | 'Other';
  principal: number;
  annualRate: number;
  tenureMonths: number;
  startDate: string;
  notes?: string;
  payments: Payment[];
}

export interface Payment {
  id: string;
  loanId: string;
  monthIndex: number;
  monthLabel: string;
  paidOn: string;
  amount: number;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function mapLoan(row: any, payments: Payment[]): Loan {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    lender: row.lender,
    type: row.loan_type as Loan['type'],
    principal: Number(row.principal),
    annualRate: Number(row.annual_rate),
    tenureMonths: row.tenure_months,
    startDate: row.start_date,
    notes: row.notes || '',
    payments,
  };
}

function mapPayment(row: any): Payment {
  return {
    id: row.id,
    loanId: row.loan_id,
    monthIndex: row.month_index,
    monthLabel: row.month_label,
    paidOn: row.paid_on,
    amount: Number(row.amount),
  };
}

export const loanService = {
  async getAll(): Promise<Loan[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    try {
      const { data: loansData, error: loansError } = await supabase
        .from('loans')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (loansError) {
        if (isSchemaError(loansError)) throw loansError;
        return [];
      }

      const { data: paymentsData, error: paymentsError } = await supabase
        .from('loan_payments')
        .select('*')
        .eq('user_id', user.id)
        .order('month_index', { ascending: true });

      if (paymentsError) {
        if (isSchemaError(paymentsError)) throw paymentsError;
        return (loansData || []).map((l) => mapLoan(l, []));
      }

      const paymentsByLoan: Record<string, Payment[]> = {};
      (paymentsData || []).forEach((p) => {
        if (!paymentsByLoan[p.loan_id]) paymentsByLoan[p.loan_id] = [];
        paymentsByLoan[p.loan_id].push(mapPayment(p));
      });

      return (loansData || []).map((l) => mapLoan(l, paymentsByLoan[l.id] || []));
    } catch (error: any) {
      console.error('loanService.getAll error:', error.message);
      throw error;
    }
  },

  async getById(id: string): Promise<Loan | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    try {
      const { data: loanData, error: loanError } = await supabase
        .from('loans')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (loanError) {
        if (loanError.code === 'PGRST116') return null;
        if (isSchemaError(loanError)) throw loanError;
        return null;
      }

      const { data: paymentsData } = await supabase
        .from('loan_payments')
        .select('*')
        .eq('loan_id', id)
        .order('month_index', { ascending: true });

      return mapLoan(loanData, (paymentsData || []).map(mapPayment));
    } catch (error: any) {
      console.error('loanService.getById error:', error.message);
      throw error;
    }
  },

  async create(loan: Omit<Loan, 'id' | 'userId' | 'payments'>): Promise<Loan | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('loans')
        .insert({
          user_id: user.id,
          name: loan.name,
          lender: loan.lender,
          loan_type: loan.type,
          principal: loan.principal,
          annual_rate: loan.annualRate,
          tenure_months: loan.tenureMonths,
          start_date: loan.startDate,
          notes: loan.notes || '',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      return mapLoan(data, []);
    } catch (error: any) {
      console.error('loanService.create error:', error.message);
      throw error;
    }
  },

  async update(id: string, loan: Partial<Omit<Loan, 'id' | 'userId' | 'payments'>>): Promise<Loan | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (loan.name !== undefined) updateData.name = loan.name;
      if (loan.lender !== undefined) updateData.lender = loan.lender;
      if (loan.type !== undefined) updateData.loan_type = loan.type;
      if (loan.principal !== undefined) updateData.principal = loan.principal;
      if (loan.annualRate !== undefined) updateData.annual_rate = loan.annualRate;
      if (loan.tenureMonths !== undefined) updateData.tenure_months = loan.tenureMonths;
      if (loan.startDate !== undefined) updateData.start_date = loan.startDate;
      if (loan.notes !== undefined) updateData.notes = loan.notes;

      const { data, error } = await supabase
        .from('loans')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      const { data: paymentsData } = await supabase
        .from('loan_payments')
        .select('*')
        .eq('loan_id', id)
        .order('month_index', { ascending: true });

      return mapLoan(data, (paymentsData || []).map(mapPayment));
    } catch (error: any) {
      console.error('loanService.update error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('loans')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (error: any) {
      console.error('loanService.delete error:', error.message);
      throw error;
    }
  },

  async addPayment(loanId: string, payment: Omit<Payment, 'id' | 'loanId'>): Promise<Payment | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Guard: prevent duplicate payment for the same month
      const { data: existing } = await supabase
        .from('loan_payments')
        .select('id')
        .eq('loan_id', loanId)
        .eq('user_id', user.id)
        .eq('month_label', payment.monthLabel)
        .limit(1);

      if (existing && existing.length > 0) {
        // Already paid this month — return null silently
        return null;
      }

      const { data, error } = await supabase
        .from('loan_payments')
        .insert({
          loan_id: loanId,
          user_id: user.id,
          month_index: payment.monthIndex,
          month_label: payment.monthLabel,
          paid_on: payment.paidOn,
          amount: payment.amount,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      return mapPayment(data);
    } catch (error: any) {
      console.error('loanService.addPayment error:', error.message);
      throw error;
    }
  },

  async deleteLastPayment(loanId: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data: payments } = await supabase
        .from('loan_payments')
        .select('id, month_index')
        .eq('loan_id', loanId)
        .eq('user_id', user.id)
        .order('month_index', { ascending: false })
        .limit(1);

      if (!payments || payments.length === 0) return false;

      const { error } = await supabase
        .from('loan_payments')
        .delete()
        .eq('id', payments[0].id);

      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (error: any) {
      console.error('loanService.deleteLastPayment error:', error.message);
      throw error;
    }
  },
};

export function getLoanMetrics(loan: Loan) {
  const emi = calculateEMI(loan.principal, loan.annualRate, loan.tenureMonths);
  const amort = generateAmortization(loan.principal, loan.annualRate, loan.tenureMonths, new Date(loan.startDate));
  const paidCount = loan.payments.length;
  const remainingMonths = Math.max(0, loan.tenureMonths - paidCount);
  const percentComplete = Math.min(100, Math.round((paidCount / loan.tenureMonths) * 100));

  let outstandingBalance = loan.principal;
  if (paidCount > 0 && amort[paidCount - 1]) {
    outstandingBalance = amort[paidCount - 1].closingBalance;
  } else if (paidCount >= loan.tenureMonths) {
    outstandingBalance = 0;
  }

  const totalInterestPaid = loan.payments.reduce((sum, p, idx) => {
    const row = amort[idx];
    return sum + (row ? row.interest : 0);
  }, 0);

  const totalPrincipalPaid = loan.principal - outstandingBalance;

  const nextDueIndex = paidCount;
  const startD = new Date(loan.startDate);
  const nextDue = new Date(startD);
  nextDue.setMonth(nextDue.getMonth() + nextDueIndex);

  const endDate = new Date(startD);
  endDate.setMonth(endDate.getMonth() + loan.tenureMonths - 1);

  const totalInterestPayable = emi * loan.tenureMonths - loan.principal;

  return {
    emi,
    paidCount,
    remainingMonths,
    percentComplete,
    outstandingBalance,
    totalInterestPaid,
    totalPrincipalPaid,
    nextDue,
    endDate,
    totalInterestPayable,
    amort,
    status: getEMIStatus(nextDue.toISOString(), false),
  };
}
